import classNames from 'classnames';
import CostCodeCapsules from 'components/domain/cost-code/CostCodeCapsules/CostCodeCapsules';
import EquipmentCapsules from 'components/domain/equipment/EquipmentCapsules/EquipmentCapsules';
import MemberCapsules from 'components/domain/member/MemberCapsules/MemberCapsules';
import ProjectCapsules from 'components/domain/project/ProjectCapsules/ProjectCapsules';
import TimeEntryActionBar from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBar';
import CostCodeTimeEntryDataReport from 'components/domain/time-entry/TimeEntryDataReport/CostCodeTimeEntryDataReport/CostCodeTimeEntryDataReport';
import useCostCodeTimeEntryDataReportData from 'components/domain/time-entry/TimeEntryDataReport/CostCodeTimeEntryDataReport/hooks/useCostCodeTimeEntryDataReportData';
import { ITimeEntryDataTableRow } from 'components/domain/time-entry/TimeEntryDataTable/TimeEntryDataTable';
import ManagedTimeRangeReportDialog from 'components/foundation/dialogs/ManagedTimeRangeReportDialog/ManagedTimeRangeReportDialog';
import { defaultTimeEntryColumns } from 'containers/timesheets/hooks/useTimesheetsColumns';
import { useReduxSelector } from 'hooks';
import { first, isNil, map, partition, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateCheckedTimeEntryIds, updateCheckedTimeOffIds } from 'store/TimeEntryTable/TimeEntryTable';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { dateUtils } from 'utils';
import { onlyHasRemainingItem } from 'utils/collectionUtils';
import { areTimeRangesEqual } from 'utils/dateUtils';
import { t } from 'utils/localize';
import TimeEntryFilterBar from '../../TimeEntryFilterBar/TimeEntryFilterBar';
import './CostCodeTimeEntryDialog.scss';

export interface ICostCodeTimeEntryDialogProps {
  className?: string | object;
  isOpen: boolean;
  onClose: () => void;
  timeRange: ITimeRange<DateTime>;
  timeRangeType: TimeRangeType;
  costCodeIds?: string[] | null;
  projectIds?: string[] | null;
  memberIds?: string[] | null;
  equipmentIds?: string[] | null;
  includeOpen?: boolean;
  excludeDeleted?: boolean;
  projectIdWithDescendants?: boolean;
  timeRangeBackPressed?: () => boolean; // returns boolean to indicate if the event consumed (true) or if it should perform the default behavior (false)
  timeRangeForwardPressed?: () => boolean; // returns boolean to indicate if the event consumed (true) or if it should perform the default behavior (false)
  onDataChange?: (ids: string[]) => void;
}

const CostCodeTimeEntryDialog = ({
  className,
  isOpen,
  onClose,
  timeRange,
  timeRangeType,
  costCodeIds,
  projectIds,
  memberIds,
  equipmentIds,
  includeOpen,
  excludeDeleted,
  projectIdWithDescendants,
  timeRangeBackPressed,
  timeRangeForwardPressed,
  onDataChange,
}: ICostCodeTimeEntryDialogProps) => {
  const filterCostCodeIds = useMemo(() => (costCodeIds ? costCodeIds : undefined), [costCodeIds]);
  const filterProjectIds = useMemo(() => (projectIds ? projectIds : undefined), [projectIds]);
  const filterMemberIds = useMemo(() => (memberIds ? memberIds : undefined), [memberIds]);
  const filterEquipmentIds = useMemo(() => (equipmentIds ? equipmentIds : undefined), [equipmentIds]);

  const dispatch = useDispatch();
  const selectedIds = useReduxSelector((state) =>
    state.timeEntryTable.checkedTimeEntryIds.concat(state.timeEntryTable.checkedTimeOffIds)
  );

  const onSelect = useCallback(
    (rows: ITimeEntryDataTableRow[]) => {
      const [timeEntryIds, timeOffIds] = partition(rows, (row) => isNil(row.timeOffType));
      dispatch(updateCheckedTimeEntryIds(map(timeEntryIds, 'id')));
      dispatch(updateCheckedTimeOffIds(map(timeOffIds, 'id')));
    },
    [dispatch]
  );

  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [filterRange, setFilterRange] = useState<ITimeRange<DateTime>>(timeRange);

  const { data, loading, loadedAll, loadAll, updateCostCodeIds } = useCostCodeTimeEntryDataReportData(
    scroller,
    filterRange,
    includeOpen,
    filterCostCodeIds,
    filterEquipmentIds,
    filterMemberIds,
    filterProjectIds,
    excludeDeleted,
    false,
    projectIdWithDescendants
  );

  function updateData(ids: string[]) {
    onDataChange?.(ids);
    updateCostCodeIds(ids);
  }

  function renderTimeEntryReport(range: ITimeRange<DateTime>) {
    if (!areTimeRangesEqual(filterRange, range)) {
      setFilterRange(range);
    }
    return (
      <CostCodeTimeEntryDataReport
        columnSettings={defaultTimeEntryColumns}
        updateCostCodeIds={updateData}
        timeRange={range}
        selectedIds={selectedIds}
        onSelectChange={onSelect}
        data={data}
        loading={loading}
        memberIds={memberIds}
        projectId={!onlyHasRemainingItem(projectIds) ? first(projectIds) : undefined}
        equipmentId={!onlyHasRemainingItem(equipmentIds) ? first(equipmentIds) : undefined}
        loadedAll={loadedAll}
        applyPrintOptions={false}
      />
    );
  }

  function renderActionBar() {
    return (
      <TimeEntryActionBar
        onBulkEditSubmit={(entries) => {
          const ids = uniq(entries.map((entry) => entry.costCode?.id ?? '')).filter((id) => id.length > 0);
          updateData(ids);
        }}
        onDelete={() => {
          updateData(data.map((d) => d.id));
        }}
        onTimeEntryEdit={(data) => {
          const ids = data.members;
          updateData(ids);
        }}
      />
    );
  }

  function renderFilterBar(range: ITimeRange<DateTime>) {
    const exportFileName = `busybusy-${t(
      'cost-code-activity-report-time-entries'
    )}-${dateUtils.isoTimeStampLocal()}.csv`;

    return (
      <>
        {(filterMemberIds || filterProjectIds || filterCostCodeIds || filterEquipmentIds) && (
          <TimeEntryFilterBar
            timeRange={range}
            data={data}
            loadedAll={loadedAll}
            loadAll={loadAll}
            exportFileName={exportFileName}
            renderFilters={(forPrint) => (
              <>
                {filterMemberIds && (
                  <MemberCapsules className="ml-2" printVersion={forPrint} memberIds={filterMemberIds} />
                )}
                {filterProjectIds && (
                  <ProjectCapsules
                    className="ml-2"
                    projectIds={filterProjectIds}
                    printVersion={forPrint}
                    isUnassinged={projectIdWithDescendants === false}
                  />
                )}
                {filterEquipmentIds && (
                  <EquipmentCapsules className="ml-2" printVersion={forPrint} equipmentIds={filterEquipmentIds} />
                )}
                {/* We're not printing the cost code capsules because the titles are already on the table headers */}
                {filterCostCodeIds && !forPrint && (
                  <CostCodeCapsules className="ml-2" printVersion={forPrint} costCodeIds={filterCostCodeIds} />
                )}
              </>
            )}
          />
        )}
      </>
    );
  }

  const classes = classNames('cost-code-time-entry-dialog', className);

  return (
    <ManagedTimeRangeReportDialog
      className={classes}
      isOpen={isOpen}
      onClose={onClose}
      timeRange={timeRange}
      timeRangeType={timeRangeType}
      reportComponent={renderTimeEntryReport}
      title={t('Time Entries')}
      renderActionBar={renderActionBar}
      renderFilterBar={renderFilterBar}
      setRef={setScroller}
      timeRangeBackPressed={timeRangeBackPressed}
      timeRangeForwardPressed={timeRangeForwardPressed}
      headerClassName={'no-print'}
    />
  );
};

export default CostCodeTimeEntryDialog;
